import { useSyncExternalStore } from 'react';
import { Platform } from 'react-native';

/**
 * CORE-1131 — адрес вкладки «Мои Live-сессии».
 *
 * Верхняя вкладка профиля синхронизируется с `#<tab>` давно, поэтому refresh и
 * «назад» её переживают. А вот раздел внутри вкладки не переживал ничего:
 * перезагрузка возвращала человека к записям, даже если он полчаса работал с
 * сериями. Задача требует обратного, и держаться это должно на адресе, а не на
 * памяти компонента — только адрес и переживает перезагрузку.
 *
 * Грамматика: `#rooms/<раздел>[/<подвкладка>]`. Со списком модалок
 * (`create-room`, `create-live`) она не конфликтует: тот закрытый, и `parseHash`
 * второй сегмент из него не узнаёт — вернёт `modal: null`, как и раньше.
 *
 * Чистый модуль без React: разбор адреса — правило, и проверять его рендером
 * вкладки со всеми её запросами значит не проверять ничего.
 */

/** Верхние разделы вкладки. */
export type LiveTabSection = 'recordings' | 'management' | 'rooms';

/** Подвкладки «Управления». */
export type LiveManagementSubTab = 'upcoming' | 'series' | 'templates';

export const LIVE_TAB_SECTIONS: LiveTabSection[] = ['recordings', 'management', 'rooms'];
export const LIVE_MANAGEMENT_SUBTABS: LiveManagementSubTab[] = ['upcoming', 'series', 'templates'];

/**
 * Раздел по умолчанию.
 *
 * До CORE-1526 это были «Записи» — прежнее содержимое вкладки. Решение
 * владельца (03.10): при открытии «Моих Live-сессий» показываем
 * «Предстоящие» — ближайшее по времени, архив в конце.
 */
export const DEFAULT_SECTION: LiveTabSection = 'management';
export const DEFAULT_SUBTAB: LiveManagementSubTab = 'upcoming';

export interface LiveTabRoute {
  section: LiveTabSection;
  subTab: LiveManagementSubTab;
}

/**
 * Разобрать адрес. Неизвестное значение — не ошибка, а старая ссылка: человек
 * должен попасть на вкладку, а не в пустоту.
 */
export function parseLiveTabHash(raw: string | null | undefined): LiveTabRoute {
  const parts = (raw ?? '').replace(/^#/, '').split('/');
  // Первый сегмент — сама вкладка профиля, её разбирает `dashboardTabs`.
  const sectionRaw = parts[1];
  const subTabRaw = parts[2];
  return {
    section: (LIVE_TAB_SECTIONS as string[]).includes(sectionRaw)
      ? (sectionRaw as LiveTabSection)
      : DEFAULT_SECTION,
    subTab: (LIVE_MANAGEMENT_SUBTABS as string[]).includes(subTabRaw)
      ? (subTabRaw as LiveManagementSubTab)
      : DEFAULT_SUBTAB,
  };
}

/**
 * CORE-1206 — разделы, которые принадлежат МАСТЕРСКОЙ части вкладки.
 *
 * Вкладка «Мои Live-сессии» есть и у обычного пользователя: свою Live-сессию
 * он создавать вправе (действующая матрица прав, подтверждена в тикете). А вот
 * записи чужих эфиров и управление расписанием — мастерские поверхности, и
 * оказались они у всех по недосмотру: разделы рисовались по одному условию
 * «это моя вкладка, и я не смотрю чужой профиль», без единого упоминания роли.
 *
 * Обычный пользователь, зашедший по прямой ссылке `/dashboard#rooms`, попадал
 * на раздел записей — он же {@link DEFAULT_SECTION} — и видел мастерскую
 * панель, а вместо содержимого «Не удалось обработать ответ сервера»: сервер
 * ему эти данные, разумеется, не отдаёт. QA на staging завела это как блокер
 * релиза (CORE-1206).
 */
export const MASTER_ONLY_SECTIONS: LiveTabSection[] = ['recordings', 'management'];

/** Разделы вкладки, доступные этому человеку. Порядок — как в интерфейсе. */
export function allowedLiveTabSections(isMaster: boolean): LiveTabSection[] {
    return isMaster
        ? LIVE_TAB_SECTIONS
        : LIVE_TAB_SECTIONS.filter(s => !(MASTER_ONLY_SECTIONS as string[]).includes(s));
}

/**
 * Привести адрес вкладки к тому, что этому человеку вообще доступно.
 *
 * Мастеру — как есть. Всем остальным раздел заменяется на единственный их
 * собственный: «Совместные практики». Подвкладка «Управления» при этом теряет
 * смысл и возвращается к значению по умолчанию — хвост `#rooms/rooms/series`
 * обещал бы раздел, которого у человека нет.
 *
 * Возвращать сюда `null` и «ничего не показывать» было бы хуже: вкладка у него
 * законная, и пустой экран вместо своих практик читается как поломка.
 */
export function resolveLiveTabRoute(route: LiveTabRoute, isMaster: boolean): LiveTabRoute {
    if (isMaster) return route;
    const allowed = allowedLiveTabSections(false);
    if (allowed.includes(route.section)) return route;
    return { section: allowed[0], subTab: DEFAULT_SUBTAB };
}

/**
 * Собрать адрес. Подвкладка пишется только у «Управления»: у записей её нет, и
 * хвост `#rooms/recordings/upcoming` обещал бы несуществующий раздел.
 */
export function buildLiveTabHash(route: LiveTabRoute): string {
  if (route.section !== 'management') return `#rooms/${route.section}`;
  return `#rooms/management/${route.subTab}`;
}

/**
 * Текущий адрес — или значения по умолчанию там, где адреса нет вовсе.
 *
 * Снимок кэшируется по строке адреса: `useSyncExternalStore` сравнивает
 * результат по ссылке и на новом объекте при каждом чтении зациклил бы рендер.
 */
let snapshotHash: string | null = null;
let snapshot: LiveTabRoute = { section: DEFAULT_SECTION, subTab: DEFAULT_SUBTAB };

/**
 * CORE-1134 — одноразовое намерение «оказаться в этом разделе».
 *
 * Экран может попросить раздел ПАРАМЕТРОМ навигации (визард шаблонов ведёт на
 * «Управление → Шаблоны»), но записать hash в момент перехода нельзя: роутер
 * переписывает URL собственным эффектом ПОЗЖЕ дочерних, и запись ложилась бы
 * на ещё не сменённый pathname, а затем стиралась вместе с ним — итог
 * «Записи» вместо «Шаблонов».
 *
 * Поэтому намерение не пишет адрес само. Пока оно живо, {@link readLiveTabRoute}
 * отдаёт его вместо разбора hash, а писатель адреса в дашборде, читающий этот
 * же снимок, сам доводит hash до намерения — сколько бы раз роутер ни
 * переписывал URL под ним. Гаснет намерение по короткому сроку жизни, а не по
 * первому совпадению адреса: совпадение случается ещё на URL визарда, роутер
 * после него переписывает URL заново — и погашенное намерение уже некому было
 * бы доводить (ровно так «Шаблоны» и превращались обратно в «Записи»). Явный
 * переход человека ({@link writeLiveTabRoute}) гасит его сразу; после — адрес
 * снова единственный источник (CORE-1131).
 */
let pendingIntent: LiveTabRoute | null = null;
let pendingIntentUntil = 0;
let intentTick: ReturnType<typeof setInterval> | null = null;
/**
 * CORE-1526 (QA FAIL 08.10, п. 19) — сколько `popstate` намерение обязано
 * пережить. Возврат из визарда идёт историей (`goBack`, см. `wizardReturn`),
 * а на вебе React Navigation делает это через `history.go(-1)` — и браузер
 * поднимает `popstate`, тот же, что на «назад» человека. Слушатель ниже гасил
 * намерение по нему, роутер следом переписывал адрес без hash — и «Выйти» из
 * нового шаблона приземлял в «Предстоящие». Тут не человек, а наш собственный
 * переход: один `popstate` намерение пропускает мимо.
 */
let intentPopsToSurvive = 0;
/**
 * С какого адреса намерение поставлено. Возврат из визарда обязан сменить
 * pathname — пока он прежний, совпадение hash ничего не значит (адрес визарда
 * роутер ещё перепишет). Намерение, поставленное уже в кабинете (параметр
 * навигации), сменой пути не связано.
 */
let intentOriginPath: string | null = null;
let intentSettleTicks = 0;

/**
 * Срок жизни намерения — предохранитель, а не механизм.
 *
 * Роутер переписывает URL с непредсказуемой задержкой и стирает hash без
 * какого-либо события. Раньше срок был три секунды, и на медленной машине с
 * тяжёлым кабинетом (QA 13.10, п. 19) роутер успевал переписать адрес уже
 * ПОСЛЕ того, как намерение истекло, — «Выйти» из шаблона снова приземлял в
 * «Предстоящие». Теперь намерение гаснет не по часам, а когда адрес ДОВЕДЁН
 * (см. `INTENT_SETTLE_TICKS`); часы лишь страхуют от зависшего намерения.
 * «Назад» человека гасит его сразу (см. `subscribeToLiveTabRoute`), явный
 * выбор раздела — тоже (`writeLiveTabRoute`).
 */
const INTENT_TTL_MS = 10_000;
/**
 * Сколько подряд проверок (по 200 мс) адрес должен совпадать с намерением,
 * чтобы считать его доведённым. Одного совпадения мало: оно случается ещё на
 * адресе визарда, а роутер после него переписывает URL заново.
 */
const INTENT_SETTLE_TICKS = 3;

export interface LiveTabRouteIntentOptions {
  /**
   * Намерение ставится перед возвратом по истории: первый `popstate` — это
   * сам возврат, а не «назад» человека, и гасить намерение по нему нельзя.
   * Второй и дальше — уже человек. Живёт столько же, сколько намерение.
   */
  survivesHistoryPop?: boolean;
}

/**
 * CORE-2002 — адрес, на котором hash раздела вообще что-то значит: пути
 * дашборда. В таблице маршрутов App.tsx экран дашборда стоит дважды:
 * `Dashboard: 'dashboard'` и `Me: 'me'`.
 */
export function isDashboardPath(pathname: string | null | undefined): boolean {
  const p = (pathname ?? '').replace(/\/+$/, '');
  return p === '/dashboard' || p === '/me';
}

export function setLiveTabRouteIntent(route: LiveTabRoute, options?: LiveTabRouteIntentOptions): void {
  pendingIntent = route;
  pendingIntentUntil = Date.now() + INTENT_TTL_MS;
  intentPopsToSurvive = options?.survivesHistoryPop ? 1 : 0;
  intentOriginPath = options?.survivesHistoryPop && Platform.OS === 'web' && typeof window !== 'undefined'
    ? window.location.pathname
    : null;
  intentSettleTicks = 0;
  notifyLiveTabRoute();
  // Доводчик работает в самом сторе, а не в React: извещение не будит
  // писателя адреса (снимок ссылочно тот же — рендера нет), а роутер стирает
  // hash без события, и слушать нечего. Поэтому hash дописывается напрямую к
  // тому адресу, где бы роутер его ни оставил, пока намерение живо.
  if (intentTick != null) clearInterval(intentTick);
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  intentTick = setInterval(() => {
    if (pendingIntent == null || Date.now() >= pendingIntentUntil) {
      if (intentTick != null) clearInterval(intentTick);
      intentTick = null;
      return;
    }
    // CORE-2002 — только на адресе самого дашборда. Пока роутер не довёл
    // путь до дашборда, ждём следующего тика; а ушёл человек с дашборда
    // (виджет Агапеллы увёл в /chats) — чужой адрес не трогаем: раньше
    // доводчик дописывал `#rooms/…` и к визарду, и к /chats.
    if (!isDashboardPath(window.location.pathname)) return;
    const target = buildLiveTabHash(pendingIntent);
    if (window.location.hash !== target) {
      intentSettleTicks = 0;
      // `replaceState`, а не `pushState`: доводка не событие навигации, и
      // засорять историю её повторами нельзя.
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${target}`);
      notifyLiveTabRoute();
      return;
    }
    // Адрес совпал и держится: намерение исполнено, дальше адрес — единственный
    // источник (CORE-1131). Пока путь тот же, с которого ушли, — ещё не
    // вернулись, совпадение не считается.
    if (intentOriginPath != null && window.location.pathname === intentOriginPath) return;
    intentSettleTicks += 1;
    if (intentSettleTicks >= INTENT_SETTLE_TICKS) clearLiveTabRouteIntent();
  }, 200);
}

export function readLiveTabRoute(): LiveTabRoute {
  if (pendingIntent != null && Date.now() >= pendingIntentUntil) pendingIntent = null;
  if (Platform.OS !== 'web' || typeof window === 'undefined') return pendingIntent ?? snapshot;
  if (pendingIntent != null) return pendingIntent;
  const raw = window.location.hash;
  if (raw !== snapshotHash) {
    snapshotHash = raw;
    snapshot = parseLiveTabHash(raw);
  }
  return snapshot;
}

/**
 * CORE-1131 (QA FAIL 02.09) — адрес стал ЕДИНСТВЕННЫМ источником, а не вторым.
 *
 * До этого раздел жил и в адресе, и в состоянии компонента, а между ними стояли
 * подписка на `popstate` и эффект, пишущий адрес. Два источника с двусторонней
 * синхронизацией расходятся при первой же гонке — QA сняла это дважды: сперва
 * адрес возвращался к `#rooms`, потом содержимое и подсветка подвкладки после
 * «назад» показывали разное.
 *
 * Теперь состояния нет вовсе: и содержимое, и подсветка читают один и тот же
 * снимок адреса в одном и том же рендере. Разойтись им технически негде.
 *
 * Подписка слушает и `popstate`, и `hashchange`: первый приходит на навигацию
 * по истории, второй — когда меняется только фрагмент, и полагаться лишь на
 * первый в чужом навигаторе оказалось нельзя. Программная запись не поднимает
 * ни того, ни другого, поэтому {@link writeLiveTabRoute} извещает сама.
 */
const listeners = new Set<() => void>();

/**
 * CORE-1131 (QA FAIL 03.09) — «назад» гасит намерение.
 *
 * Намерение (CORE-1134) на три секунды становится сильнее адреса: пока оно
 * живо, {@link readLiveTabRoute} возвращает ЕГО, не заглядывая в hash, а
 * доводчик каждые 200 мс переписывает hash под него. Для доводки после визарда
 * это верно, а для человека — нет: он нажал «назад», браузер вернул адрес, и
 * через мгновение доводчик утащил его обратно. Снаружи это ровно то, что
 * снимала QA: адрес — «Регулярные серии», подсветка — «Шаблоны».
 *
 * Побочный эффект был описан в комментарии к сроку жизни намерения как
 * известный. Известный — не значит допустимый: явное действие человека обязано
 * побеждать трёхсекундный доводчик.
 *
 * Гасим именно по `popstate`, и только по нему. Доводчик пишет адрес через
 * `replaceState`, роутер — своими средствами; ни то, ни другое события не
 * поднимает. Значит `popstate` — это человек в истории браузера, и другого
 * источника у него здесь нет.
 */
export function clearLiveTabRouteIntent(): void {
  pendingIntent = null;
  pendingIntentUntil = 0;
  intentPopsToSurvive = 0;
  intentOriginPath = null;
  intentSettleTicks = 0;
  if (intentTick != null) {
    clearInterval(intentTick);
    intentTick = null;
  }
}

/**
 * Обёртки над `popstate` по подписчику: `removeEventListener` снимает слушателя
 * только по той же ссылке, а вешаем мы уже не исходную функцию.
 */
const popHandlers = new Map<() => void, () => void>();

export function subscribeToLiveTabRoute(listener: () => void): () => void {
  listeners.add(listener);
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const onPop = () => {
      // Наш собственный возврат по истории (см. `intentPopsToSurvive`):
      // намерение остаётся, доводчик доведёт адрес, подписчик всё равно
      // извещается — адрес под ним сменился.
      if (pendingIntent != null && Date.now() < pendingIntentUntil && intentPopsToSurvive > 0) {
        intentPopsToSurvive -= 1;
      } else {
        clearLiveTabRouteIntent();
      }
      listener();
    };
    popHandlers.set(listener, onPop);
    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', listener);
  }
  return () => {
    listeners.delete(listener);
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const onPop = popHandlers.get(listener);
      if (onPop) {
        window.removeEventListener('popstate', onPop);
        popHandlers.delete(listener);
      }
      window.removeEventListener('hashchange', listener);
    }
  };
}

/** Известить подписчиков: `pushState`/`replaceState` событий не поднимают. */
export function notifyLiveTabRoute(): void {
  listeners.forEach((l) => l());
}

/**
 * Перейти к разделу. `pushState`, а не `replaceState`: «назад» должен
 * возвращать к предыдущему разделу, а не уводить со всей вкладки сразу.
 */
export function writeLiveTabRoute(route: LiveTabRoute): void {
  // Явный переход гасит намерение (CORE-1134): человек выбрал раздел сам,
  // и доводить адрес до старого намерения значило бы вырывать выбор из рук.
  pendingIntent = null;
  intentPopsToSurvive = 0;
  intentOriginPath = null;
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  const target = buildLiveTabHash(route);
  if (window.location.hash === target) return;
  window.history.pushState(null, '', `${window.location.pathname}${window.location.search}${target}`);
  notifyLiveTabRoute();
}

/** Раздел и подвкладка так, как их прямо сейчас называет адрес страницы. */
export function useLiveTabRoute(): LiveTabRoute {
  return useSyncExternalStore(subscribeToLiveTabRoute, readLiveTabRoute, readLiveTabRoute);
}

/** Только для тестов: сбросить кэш снимка между случаями. */
export function _resetLiveTabRouteForTest(): void {
  snapshotHash = null;
  snapshot = { section: DEFAULT_SECTION, subTab: DEFAULT_SUBTAB };
  pendingIntent = null;
  intentPopsToSurvive = 0;
  intentOriginPath = null;
  intentSettleTicks = 0;
  if (intentTick != null) { clearInterval(intentTick); intentTick = null; }
  listeners.clear();
}

/**
 * CORE-1526 (п. 18) — разделы «Моих Live-сессий» одним рядом.
 *
 * Аудит 28.09: «Предстоящие», «Регулярные» и «Шаблоны» лежали внутри
 * «Управления», и до расписания было два перехода. Маршруты те же
 * (`buildLiveTabHash`), меняется только то, что все кнопки видны сразу.
 *
 * Решения владельца (03.10): сначала ближайшие и предстоящие, архив
 * («Записи») в конце; раздел с регулярными Live-сессиями называется
 * «Регулярные Live-сессии», а не «Серии»; «Совместные практики» пока без
 * изменений.
 */
export interface FlatLiveSection {
  key: string;
  label: string;
  route: { section: 'management'; subTab: LiveManagementSubTab } | { section: 'recordings' | 'rooms' };
}

export function flatLiveSections(allowed: readonly LiveTabSection[]): FlatLiveSection[] {
  const out: FlatLiveSection[] = [];
  if (allowed.includes('management')) {
    out.push(
      { key: 'management-upcoming', label: 'Предстоящие', route: { section: 'management', subTab: 'upcoming' } },
      { key: 'management-series', label: 'Регулярные Live-сессии', route: { section: 'management', subTab: 'series' } },
      { key: 'management-templates', label: 'Шаблоны', route: { section: 'management', subTab: 'templates' } },
    );
  }
  if (allowed.includes('rooms')) out.push({ key: 'rooms', label: 'Совместные практики', route: { section: 'rooms' } });
  if (allowed.includes('recordings')) out.push({ key: 'recordings', label: 'Записи', route: { section: 'recordings' } });
  return out;
}
